import type { ViewerShell } from '../shell';
import type { Viewport } from '../viewport';

/**
 * "Reorientación de los cortes" (IMEXHS `sliceReorientation`, off by default; spec sections 1.4 and 7):
 * rotate / flip each series so it shows in standard radiological orientation, whatever the scanner's
 * row/column directions — axial: patient's left on screen right, posterior down; coronal: left right,
 * feet down; sagittal: posterior right, feet down. Found by trying the 8 rotation × flip combinations
 * and keeping the one whose screen axes best match those patient directions (DICOM LPS).
 * Source of the setting, as in IMEXHS: config → localStorage "_wcg_sliceReorientation" → user preference.
 */

type Vec3 = [number, number, number];
interface Candidate { rotation: number; hflip: boolean }

const STORAGE_KEY = '_wcg_sliceReorientation';
const ROTATIONS = [0, 90, 180, 270];
/** Screen +x / +y targets per plane, in patient LPS. */
const TARGETS: Record<'axial' | 'coronal' | 'sagittal', [Vec3, Vec3]> = {
  axial: [[1, 0, 0], [0, 1, 0]],
  coronal: [[1, 0, 0], [0, 0, -1]],
  sagittal: [[0, 1, 0], [0, 0, -1]],
};

let enabled = false;
/** Set once the user's saved preference was applied (it wins over config and localStorage). */
let fromPreference = false;
let shellRef: ViewerShell | null = null;

function storedSetting(): boolean | undefined {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === null ? undefined : value === 'true';
  } catch {
    return undefined;
  }
}

/** Turns reorientation on or off and re-applies it to what is on screen. */
export function setSliceReorientation(on: boolean): void {
  enabled = on;
  fromPreference = true;
  shellRef?.viewports().forEach((viewport) => { if (viewport.currentImage()) apply(viewport); });
}

export function isSliceReorientationOn(): boolean {
  return enabled;
}

const dot = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a: Vec3, b: Vec3): Vec3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];

function planeOf(rows: Vec3, columns: Vec3): keyof typeof TARGETS {
  const normal = cross(rows, columns).map(Math.abs);
  if (normal[2] >= normal[0] && normal[2] >= normal[1]) return 'axial';
  return normal[1] >= normal[0] ? 'coronal' : 'sagittal';
}

/** Patient direction of screen +x and +y for the viewport's current rotation / flips. */
function screenAxes(element: HTMLElement, rowCosines: Vec3, columnCosines: Vec3): [Vec3, Vec3] {
  const core = window.cornerstone;
  const origin = core.pixelToCanvas(element, { x: 0, y: 0 });
  const alongRow = core.pixelToCanvas(element, { x: 1, y: 0 });
  const alongColumn = core.pixelToCanvas(element, { x: 0, y: 1 });
  // canvas = i * a + j * b (i: column index, along rowCosines; j: row index, along columnCosines)
  const a = { x: alongRow.x - origin.x, y: alongRow.y - origin.y };
  const b = { x: alongColumn.x - origin.x, y: alongColumn.y - origin.y };
  const det = a.x * b.y - b.x * a.y || 1;
  const toPatient = (i: number, j: number): Vec3 => [0, 1, 2].map((k) => i * rowCosines[k] + j * columnCosines[k]) as Vec3;
  // (i, j) for a unit canvas step in x and in y (inverse of [a b]).
  return [toPatient(b.y / det, -a.y / det), toPatient(-b.x / det, a.x / det)];
}

function normalize(v: Vec3): Vec3 {
  const length = Math.hypot(...v) || 1;
  return v.map((x) => x / length) as Vec3;
}

function apply(viewport: Viewport): void {
  const image = viewport.currentImage();
  if (!image) return;
  const plane = window.cornerstone.metaData.get('imagePlaneModule', image.imageId) as { rowCosines?: number[]; columnCosines?: number[] } | undefined;
  const view = window.cornerstone.getViewport(viewport.element);
  if (!view) return;
  if (!enabled || !plane?.rowCosines || !plane.columnCosines) {
    if (!enabled && viewport.element.dataset.reoriented === 'true') {
      window.cornerstone.setViewport(viewport.element, { ...view, rotation: 0, hflip: false, vflip: false });
      delete viewport.element.dataset.reoriented;
    }
    return;
  }
  const rows = plane.rowCosines.map(Number) as Vec3;
  const columns = plane.columnCosines.map(Number) as Vec3;
  const [targetX, targetY] = TARGETS[planeOf(rows, columns)];
  let best: Candidate = { rotation: 0, hflip: false };
  let bestScore = Number.NEGATIVE_INFINITY;
  for (const rotation of ROTATIONS) {
    for (const hflip of [false, true]) {
      window.cornerstone.setViewport(viewport.element, { ...view, rotation, hflip, vflip: false });
      const [x, y] = screenAxes(viewport.element, rows, columns);
      const score = dot(normalize(x), targetX) + dot(normalize(y), targetY);
      if (score > bestScore + 1e-9) {
        bestScore = score;
        best = { rotation, hflip };
      }
    }
  }
  window.cornerstone.setViewport(viewport.element, { ...view, rotation: best.rotation, hflip: best.hflip, vflip: false });
  viewport.element.dataset.reoriented = 'true';
}

export function install(shell: ViewerShell): void {
  shellRef = shell;
  if (!fromPreference) enabled = storedSetting() ?? shell.config.sliceReorientation === true;
  shell.on('series-shown', ({ viewport }) => apply(viewport));
}
